import { Action, combineReducers, createReducer, on } from '@ngrx/store'
import { CreditPortfolio } from '../model/credit-portfolio.model'
import {
  CreditCalculationStructure,
  CreditStructure,
  CreditTransaction,
} from '../model/credit-structure.model'
import { CreditStructureGroup } from '../model/credit-structure-group.model'
import { GseStructureGroup } from '../model/gse-structure-group.model'
import { GseStructure } from '../model/credit-structure.model'
import { Stratification } from '.././model/credit-stratification.model'
import { CreditTowerControls } from './../model/credit-tower.model'
import { CreditResults, SelectedScenario } from '../model/credit-results.model'
import { CreditTransactionActions } from './transaction/credit-transaction.actions'
import {
  CreditCalculationStructureActions,
  CreditStructureActions,
} from './structure/credit-structure.actions'
import { CreditResultsActions } from './results/credit-results.actions'
import { CreditTowerActions } from './tower/credit-tower.actions'
import { CreditModuleContext } from '../model/credit-routes.model'
import { CreditSubmissionStructureActions } from './submission/credit-submission.actions'
import { CreditStructureGroupFilterActions } from './group/credit-group.actions'
import { GseStructureGroupFilterActions } from './gse-group/gse-group.actions'
import * as fromGseGroupActions from './gse-group/gse-group.actions'
import * as fromGseGroup from './gse-group/gse-group.reducer'
import * as fromCreditDesign from './design/credit-design.reducer'
import * as fromCashFlows from './cash-flows/cash-flows.reducer'
import * as fromCreditAnimatedScenarios from './animated-scenarios/credit-animated-scenarios.reducer'
import * as fromStratification from './stratification/stratifciation.reducer'
import * as fromOptimization from './optimization/credit-optimization.reducer'
import * as fromCreditGroup from './group/credit-group.reducer'
import * as fromCreditCompare from './compare/credit-compare.reducer'
import * as fromCreditMetrics from './metrics/credit-metrics.reducer'
import * as fromCreditSubmission from './submission/credit-submission.reducer'
import * as fromCreditActions from './credit.actions'
import * as fromCreditGroupActions from './group/credit-group.actions'
import * as fromCreditQuote from './quote/credit-quote.reducer'

export const CREDIT_FEATURE_KEY = 'credit'

export interface State {
  moduleContext: CreditModuleContext
  creditStructures: CreditStructure[]
  creditStructureGroups: CreditStructureGroup[]
  gseStructures: GseStructure[]
  gseStructureGroups: GseStructureGroup[]
  towerControls: CreditTowerControls
  cashFlows: fromCashFlows.State
  animatedScenarios: fromCreditAnimatedScenarios.State
  stratifications: Stratification
  optimization: fromOptimization.State
  design: fromCreditDesign.State
  group: fromCreditGroup.State
  gseGroup: fromGseGroup.State
  compare: fromCreditCompare.State
  metrics: fromCreditMetrics.State
  submission: fromCreditSubmission.State
  quote: fromCreditQuote.QuoteStore
  currentPortfolio?: CreditPortfolio
  currentCalculationStructure?: CreditCalculationStructure
  currentTransaction?: CreditTransaction
  creditResults?: CreditResults
  defaultScenarioResults?: CreditResults
  portfolioMetricsScenarios?: SelectedScenario[]
  gsePortfolioIds: string[]
  gsePortfolioIdsLoaded: boolean
}

const initialState: State = {
  moduleContext: CreditModuleContext.Design,
  creditStructures: [],
  creditStructureGroups: [],
  gseStructures: [],
  gseStructureGroups: [],
  towerControls: {
    yAxisAsPercent: true,
  },
  design: fromCreditDesign.initialState,
  cashFlows: fromCashFlows.initialState,
  animatedScenarios: fromCreditAnimatedScenarios.initialState,
  stratifications: fromStratification.initialState,
  optimization: fromOptimization.initialState,
  group: fromCreditGroup.initialState,
  gseGroup: fromGseGroup.initialState,
  compare: fromCreditCompare.initialState,
  metrics: fromCreditMetrics.initialState,
  submission: fromCreditSubmission.initialState,
  quote: fromCreditQuote.initialState,
  gsePortfolioIds: [],
  gsePortfolioIdsLoaded: false,
}

const moduleContextReducer = createReducer(
  initialState.moduleContext,
  on(fromCreditActions.SetModuleContext, (_, { context }) => context),
)

const creditStructuresReducer = createReducer(
  initialState.creditStructures,
  on(
    fromCreditActions.FetchCreditStructuresForProgramSuccess,
    CreditStructureGroupFilterActions.fetchStructuresSuccess,
    (_, { creditStructures }) => creditStructures,
  ),
  on(
    fromCreditGroupActions.FetchCreditGroupMemberCalculationStructuresSuccess,
    (state, { creditStructures }) => {
      return state.map((structure) =>
        creditStructures.find((creditStucture) => creditStucture.id === structure.id)
          ? { ...structure, checked: true }
          : structure,
      )
    },
  ),
  on(fromCreditActions.ToggleCreditStructureChecked, (state, { creditStructure }) => {
    return state.map((structure) =>
      structure.id === creditStructure.id ? creditStructure : structure,
    )
  }),
  on(
    fromCreditGroupActions.RemoveCreditStructureGroup,
    fromCreditActions.ResetAllCheckedCreditStructuresAndGroups,
    (creditStructures, _) => {
      return creditStructures.map((creditStructure) => {
        return {
          ...creditStructure,
          checked: false,
        }
      })
    },
  ),
  on(CreditStructureGroupFilterActions.reset, (_) => []),
)

const creditStructureGroupsReducer = createReducer(
  initialState.creditStructureGroups,
  on(
    fromCreditGroupActions.FetchCreditStructureGroupsForProgramSuccess,
    CreditStructureGroupFilterActions.fetchGroupsSuccess,
    (_, { creditStructureGroups }) => creditStructureGroups,
  ),
  on(
    fromCreditActions.ToggleCreditStructureGroupChecked,
    (creditStructureGroups, { creditStructureGroup }) => {
      return creditStructureGroups.map((structureGroup) =>
        structureGroup.id === creditStructureGroup.id ? creditStructureGroup : structureGroup,
      )
    },
  ),
  on(
    fromCreditGroupActions.RemoveCreditStructureGroup,
    fromCreditActions.ResetAllCheckedCreditStructuresAndGroups,
    (creditStructureGroups, _) => {
      return creditStructureGroups.map((creditStructureGroup) => {
        return {
          ...creditStructureGroup,
          checked: false,
        }
      })
    },
  ),
  on(
    fromCreditGroupActions.SaveCreditStructureGroupSuccess,
    (creditStructureGroups, { creditStructureGroupEntity }) => {
      const groupFoundIndex = creditStructureGroups.findIndex(
        (creditStructureGroup) =>
          creditStructureGroup.id === creditStructureGroupEntity.creditStructureGroup.id,
      )
      const updatedGroup: CreditStructureGroup = {
        ...creditStructureGroupEntity.creditStructureGroup,
        checked: true,
      }
      if (groupFoundIndex >= 0) {
        const updatedCreditStructureGroups = [...creditStructureGroups]
        updatedCreditStructureGroups.splice(groupFoundIndex, 1, updatedGroup)
        return updatedCreditStructureGroups
      }
      return [...creditStructureGroups, updatedGroup]
    },
  ),
  on(
    fromCreditGroupActions.DeleteCreditStructureGroupSuccess,
    (creditStructureGroups, { creditStructureGroup }) =>
      creditStructureGroups.filter((group) => group.id !== creditStructureGroup.id),
  ),
  on(CreditStructureGroupFilterActions.reset, (_) => []),
)

const gseStructuresReducer = createReducer(
  initialState.gseStructures,
  on(
    GseStructureGroupFilterActions.fetchStructuresSuccess,
    (_, { gseStructures }) => gseStructures,
  ),
  on(
    fromGseGroupActions.FetchGseGroupMemberCalculationStructuresSuccess,
    (state, { gseStructures }) =>
      state.map((structure) =>
        gseStructures.find((gs) => gs.id === structure.id)
          ? { ...structure, checked: true }
          : structure,
      ),
  ),
  on(fromCreditActions.ToggleCreditStructureChecked, (state, { creditStructure }) =>
    state.map((structure) =>
      structure.id === creditStructure.id
        ? { ...structure, checked: creditStructure.checked }
        : structure,
    ),
  ),
  on(fromGseGroupActions.RemoveGseStructureFromGroup, (state, { gseStructure }) =>
    state.map((s) => (s.id === gseStructure.id ? { ...s, checked: false } : s)),
  ),
  on(
    fromGseGroupActions.RemoveGseStructureGroup,
    fromGseGroupActions.DeleteGseStructureGroupSuccess,
    fromCreditActions.ResetAllCheckedCreditStructuresAndGroups,
    (gseStructures) => gseStructures.map((s) => ({ ...s, checked: false })),
  ),
  on(GseStructureGroupFilterActions.reset, (_) => []),
)

const gseStructureGroupsReducer = createReducer(
  initialState.gseStructureGroups,
  on(
    fromGseGroupActions.FetchGseStructureGroupsForProgramSuccess,
    GseStructureGroupFilterActions.fetchGroupsSuccess,
    (_, { gseStructureGroups }) => gseStructureGroups,
  ),
  on(
    fromGseGroupActions.SaveGseStructureGroupSuccess,
    (gseStructureGroups, { gseStructureGroupEntity }) => {
      const groupFoundIndex = gseStructureGroups.findIndex(
        (gseStructureGroup) =>
          gseStructureGroup.id === gseStructureGroupEntity.gseStructureGroup.id,
      )
      const updatedGroup: GseStructureGroup = {
        ...gseStructureGroupEntity.gseStructureGroup,
        checked: true,
      }
      if (groupFoundIndex >= 0) {
        const updatedGseStructureGroups = [...gseStructureGroups]
        updatedGseStructureGroups.splice(groupFoundIndex, 1, updatedGroup)
        return updatedGseStructureGroups
      }
      return [...gseStructureGroups, updatedGroup]
    },
  ),
  on(
    fromGseGroupActions.ToggleGseStructureGroupChecked,
    (gseStructureGroups, { gseStructureGroup }) =>
      gseStructureGroups.map((group) =>
        group.id === gseStructureGroup.id ? gseStructureGroup : group,
      ),
  ),
  on(
    fromGseGroupActions.RemoveGseStructureGroup,
    fromCreditActions.ResetAllCheckedCreditStructuresAndGroups,
    (gseStructureGroups) => gseStructureGroups.map((g) => ({ ...g, checked: false })),
  ),
  on(
    fromGseGroupActions.DeleteGseStructureGroupSuccess,
    (gseStructureGroups, { gseStructureGroup }) =>
      gseStructureGroups
        .map((g) => ({ ...g, checked: false }))
        .filter((group) => group.id !== gseStructureGroup.id),
  ),
  on(GseStructureGroupFilterActions.reset, (_) => []),
)

const towerControlsReducer = createReducer(
  initialState.towerControls,
  on(CreditTowerActions.updateControls, (_, { towerControls }) => towerControls),
  on(CreditStructureActions.resetSelectedStructure, () => initialState.towerControls),
)

const currentPortfolioReducer = createReducer(
  initialState.currentPortfolio,
  on(
    fromCreditActions.FetchCreditPortfolioForSelectedProgramSuccess,
    (_, { portfolio }) => portfolio,
  ),
)

const currentCalculationStructureReducer = createReducer(
  initialState.currentCalculationStructure,
  on(
    CreditCalculationStructureActions.fetchSuccess,
    CreditCalculationStructureActions.postSuccess,
    CreditCalculationStructureActions.putSuccess,
    CreditCalculationStructureActions.createSuccess,
    CreditCalculationStructureActions.update,
    (_, { structure }) => structure,
  ),
  on(
    CreditStructureActions.updateSelectedStructure,
    CreditSubmissionStructureActions.updateSelectedSubmission,
    (state, { structure }) => {
      return {
        ...state,
        name: structure.label,
      }
    },
  ),
  on(
    CreditTransactionActions.addSuccess,
    CreditTransactionActions.updateSuccess,
    CreditTransactionActions.deleteSuccess,
    (state, { transactions }) => {
      return {
        ...state,
        transactions,
      }
    },
  ),

  on(CreditResultsActions.setSelectedScenario, (state, { scenarioId, returnPeriod }) => {
    return {
      ...state,
      selected_scenario: scenarioId,
      selected_return_period: returnPeriod,
    }
  }),
  on(CreditResultsActions.setSelectedDefaultScenario, (state, { scenarioId, returnPeriod }) => {
    return {
      ...state,
      selected_default_scenario: scenarioId,
      selected_default_return_period: returnPeriod,
    }
  }),
  on(
    fromCreditActions.SetModuleContext,
    CreditStructureActions.resetSelectedStructure,
    CreditSubmissionStructureActions.resetSelectedSubmission,
    (_) => undefined,
  ),
)

const currentTransactionReducer = createReducer(
  initialState.currentTransaction,
  on(
    CreditTransactionActions.selectSuccess,
    CreditTransactionActions.selectByTrancheSuccess,
    CreditTransactionActions.addSuccess,
    CreditTransactionActions.updateSuccess,
    (_, { transaction }) => transaction,
  ),
  on(CreditResultsActions.setCallType, (state, { callType }) =>
    state ? { ...state, call_type_selector: callType } : state,
  ),
  on(
    CreditCalculationStructureActions.postSuccess,
    CreditCalculationStructureActions.putSuccess,
    CreditCalculationStructureActions.update,
    (state, { structure }) =>
      structure.transactions.find((transaction) => transaction.name === state?.name),
  ),
  on(CreditCalculationStructureActions.fetchSuccess, (_, { structure }) =>
    structure.transactions.slice(0, 1).shift(),
  ),
  on(
    fromCreditActions.SetModuleContext,
    CreditTransactionActions.deleteSuccess,
    CreditStructureActions.resetSelectedStructure,
    CreditSubmissionStructureActions.resetSelectedSubmission,
    (_) => undefined,
  ),
)

const creditResultsReducer = createReducer(
  initialState.creditResults,
  on(CreditResultsActions.getSuccess, (_, { results }) => results),
  on(
    fromCreditActions.SetModuleContext,
    CreditResultsActions.setSelectedScenario,
    CreditStructureActions.resetSelectedStructure,
    CreditSubmissionStructureActions.resetSelectedSubmission,
    (_) => undefined,
  ),
)

const defaultScenarioResultsReducer = createReducer(
  initialState.defaultScenarioResults,
  on(CreditResultsActions.getDefaultSuccess, (_, { results }) => results),
  on(
    fromCreditActions.SetModuleContext,
    CreditResultsActions.setSelectedDefaultScenario,
    CreditStructureActions.resetSelectedStructure,
    CreditSubmissionStructureActions.resetSelectedSubmission,
    (_) => undefined,
  ),
)

const portfolioMetricsScenariosReducer = createReducer(
  initialState.portfolioMetricsScenarios,
  on(CreditResultsActions.setPortfolioMetricsScenarios, (_, { scenarios }) => scenarios),
  on(
    CreditStructureActions.resetSelectedStructure,
    CreditSubmissionStructureActions.resetSelectedSubmission,
    (_) => undefined,
  ),
)

const gsePortfolioIdsReducer = createReducer(
  initialState.gsePortfolioIds,
  on(fromCreditActions.FetchGsePortfolioIdsSuccess, (_, { gsePortfolioIds }) => gsePortfolioIds),
)

const gsePortfolioIdsLoadedReducer = createReducer(
  initialState.gsePortfolioIdsLoaded,
  // Only a genuine success marks this "loaded" — a failure (permanent 403 for a role like
  // Reinsurer, or a transient 5xx/network error; the two can't be told apart here, see
  // loadGsePortfolioIds$) must NOT be cached as loaded, so the fetch keeps retrying on
  // subsequent navigations and can recover once/if it actually succeeds. The client-list
  // selectors handle the not-yet-loaded case themselves rather than gating on this flag.
  on(fromCreditActions.FetchGsePortfolioIdsSuccess, () => true),
)

const creditReducer = combineReducers<State>({
  moduleContext: moduleContextReducer,
  creditStructures: creditStructuresReducer,
  creditStructureGroups: creditStructureGroupsReducer,
  gseStructures: gseStructuresReducer,
  gseStructureGroups: gseStructureGroupsReducer,
  towerControls: towerControlsReducer,
  cashFlows: fromCashFlows.reducer,
  animatedScenarios: fromCreditAnimatedScenarios.reducer,
  stratifications: fromStratification.reducer,
  optimization: fromOptimization.reducer,
  design: fromCreditDesign.reducer,
  group: fromCreditGroup.reducer,
  gseGroup: fromGseGroup.reducer,
  compare: fromCreditCompare.reducer,
  metrics: fromCreditMetrics.reducer,
  quote: fromCreditQuote.reducer,
  submission: fromCreditSubmission.reducer,
  currentPortfolio: currentPortfolioReducer,
  currentTransaction: currentTransactionReducer,
  currentCalculationStructure: currentCalculationStructureReducer,
  creditResults: creditResultsReducer,
  defaultScenarioResults: defaultScenarioResultsReducer,
  portfolioMetricsScenarios: portfolioMetricsScenariosReducer,
  gsePortfolioIds: gsePortfolioIdsReducer,
  gsePortfolioIdsLoaded: gsePortfolioIdsLoadedReducer,
})

export function reducer(state: State | undefined, action: Action) {
  return creditReducer(state, action)
}
